import Close from "@material-ui/icons/Close";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BusinessBox as BusinessBoxIcon,
  Clone,
  Description,
  FilledHeart,
  Like,
  Share,
} from "../../../assets/icons";
import { TextView } from "../../../components";
import GeneralDialog from "../../../components/GeneralDialogBox";
import SearchHeader from "../../../components/MobileView/SearchHeader";
import SegmentsList from "../../../components/MobileView/SegmentsList";
import WheelDescription from "../../../components/MobileView/WheelDescription";
import SegmentView from "../../../components/SegmentView";
import WaitlistFormModal from "../../../components/WaitlistModal";
import WheelComponent from "../../../components/WheelComponent";
import BusinessBox from "../../../components/WheelGallery/BusinessBox";
import useScreenSize from "../../../hooks/useScreenSize";
import Editor from "../../../pages/SegmentBuilder/Editor";
import { openGeneralDialogModal } from "../../../redux/reducers/generalDialog";
import { likeUnlikeWheel } from "../../../services/api";
import { useCloneWheel } from "../../../services/mutations";
import { colors } from "../../../utils";
import { ASSETS } from "../../../utils/config";
import { getLoggedInUser, isUserLoggedIn } from "../../../utils/helpers";
import ShareModal from "../../WheelView/ShareModal";
import * as Styled from "./styles";
import Popup from "../../../components/MobileView/Popup";
import { ReactComponent as HotbarIcon } from "@assets/images/Hotbar.svg";
import NewHotbar from "../../../components/NewHotbar/NewHotbar";
import MobileViewDialog from "../../../components/MobileViewDialog";
import {
  closeMobileViewDialog,
  openMobileViewDialog,
} from "../../../redux/reducers/mobileViewDialog";
import ShareInviteModal from "../../../components/ShareInviteModal";

const emptyNotification = {
  show: false,
  text: "",
  color: "#b9d645",
};

export default function MobileView({
  wallpaperSettings,
  wheelData = {},
  userData,
  handleSetChartData,
  selectedArc,
  onClose,
  onClickNext,
  onClickPrev,
  onClickLabel,
  wheelSegments,
  userProfile = {},
  isHiddenWheel,
  renderHiddenWheelView,
  setInviteShareModalVisible,
  inviteShareModalVisible,
  navigateToSignup,
}) {
  const isUserWheel =
    isUserLoggedIn() &&
    wheelData?.user_profile?.user_id === getLoggedInUser().id;

  const iconComponents = [
    { id: "description", component: <Description />, color: "#FFC121" },
    { id: "business", component: <BusinessBoxIcon />, color: "#99439D" },
    { id: "hotbar", component: <HotbarIcon />, color: "#C2E4FF" },
    !isUserWheel
      ? {
          id: "clone",
          // component: isUserLoggedIn() ? <Clone /> : <CloneV2 />,
          // color: isUserLoggedIn() ? "#0FADAD" : "#ebe8e8",
          component: <Clone />,
          color: "#0FADAD",
        }
      : null,
    { id: "like", component: <Like />, color: "#E25297" },
    { id: "share", component: <Share />, color: "#65558F" },
  ].filter(Boolean);

  const [selectedIcon, setSelectedIcon] = useState(iconComponents[0].id);
  const [openSegmentList, setOpenSegmentList] = useState(false);
  const [waitListModalVisible, setWaitListModalVisible] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [notification, setNotification] = useState(emptyNotification);
  const [showLabels, setShowLabels] = useState(true);
  const [openPopup, setOpenPopup] = useState(false);
  const [wheelViewOpacity, setWheelViewOpacity] = useState(1);

  const {
    bgWallpaper,
    segmentBgColor,
    currentBg,
    wallpaperHuePercentage,
    wallpaperHueColor,
  } = wallpaperSettings;
  const wallpaperTransparency = 100; // Hardcoded to 100

  const dispatch = useDispatch();
  const cloneWheel = useCloneWheel();
  const { portraitScreenRatio } = useScreenSize();

  const wheelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (wheelRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = wheelRef.current;
        // Check if user has scrolled more than 20 pixels from the bottom
        const scrollPosition = scrollHeight - scrollTop - clientHeight;
        if (scrollHeight > clientHeight && scrollPosition < 80) {
          setWheelViewOpacity(0); // Hide the wheel if at bottom
        } else {
          setWheelViewOpacity(1); // Fully visible if within 20px from the bottom
        }
      }
    };

    const wrapper = wheelRef.current;
    setOpenPopup(true);
    wrapper.addEventListener("scroll", handleScroll);

    return () => {
      wrapper.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsLiked(wheelData?.is_favourite);
  }, [wheelData]);

  useEffect(() => {
    if (openSegmentList || !selectedArc) {
      scrollToTop();
    }
  }, [openSegmentList, selectedArc]);

  useEffect(() => {
    dispatch(closeMobileViewDialog());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToTop = () => {
    if (wheelRef.current) {
      setWheelViewOpacity(1);
      wheelRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const toggleSegmentList = () => {
    setOpenSegmentList(!openSegmentList);
  };

  const handleToggleLabels = () => {
    setShowLabels(!showLabels);
  };

  const handleLikeUnlike = async () => {
    if (!isUserLoggedIn()) {
      handleMobileViewModal({ type: "favorite" });
    } else {
      try {
        const { data: response } = await likeUnlikeWheel(wheelData?.id);
        if (response.success) {
          setIsLiked(response.data.is_favourite);
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleCloneWheel = async () => {
    if (isUserLoggedIn()) {
      if (wheelData?.already_cloned) {
        showClonePopup();
      } else {
        dispatch(
          openGeneralDialogModal({
            isOpen: true,
            title1: "Are you sure you want to clone this wheel?",
            primaryButtonTitle: "Absolutely!",
            imageSrc: ASSETS.DialogImg,
            primaryButtonOnClick: () => {
              cloneWheel.mutate(
                { wheelId: wheelData?.id },
                {
                  onSuccess: (res) => {
                    setNotification({
                      ...notification,
                      show: true,
                      text: "This wheel has been cloned!",
                    });
                    setTimeout(() => setNotification(emptyNotification), 5000);
                    setSelectedIcon(iconComponents[0]?.id);
                  },
                  onError: (err) => console.log(err),
                }
              );
            },
            secondaryButtonTitle: "Go back",
            secondaryButtonOnClick: () => {
              setSelectedIcon(iconComponents[0]?.id);
            },
          })
        );
      }
    } else {
      handleMobileViewModal({ type: "clone" });
    }
  };

  const showClonePopup = () => {
    dispatch(
      openGeneralDialogModal({
        isOpen: true,
        title1: "You have already cloned this wheel!",
        primaryButtonTitle: "Create a New Clone",
        imageSrc: ASSETS.DialogImg,
        primaryButtonOnClick: () => {
          handleCloneAction("new");
        },
        secondaryButtonTitle: "Replace Previous Clone",
        secondaryButtonOnClick: () => {
          handleCloneAction("overwrite");
        },
      })
    );
  };

  const handleCloneAction = (action) => {
    cloneWheel.mutate(
      { wheelId: wheelData?.id, action },
      {
        onSuccess: (res) => {
          setNotification({
            ...notification,
            show: true,
            text: "This wheel has been cloned!",
          });
          setTimeout(() => setNotification(emptyNotification), 5000);
          setSelectedIcon(iconComponents[0]?.id);
        },
        onError: (err) => console.log(err),
      }
    );
  };

  const handlePopUpClose = () => {
    setOpenPopup(false);

    if (!isUserLoggedIn()) {
      // setTimeout(() => {
      //   dispatch(
      //     openMobileViewDialog({
      //       isOpen: true,
      //       title: "Want Your Own iYuga Account?",
      //     })
      //   );
      // }, 45000);
    }
  };

  const handleMobileViewModal = ({ type = "default" }) => {
    if (!isUserLoggedIn()) {
      if (type === "default") {
        dispatch(
          openMobileViewDialog({
            isOpen: true,
            title: "Want Your Own iYuga Account?",
            type: "default",
            wheel: wheelData,
          })
        );
      } else if (type === "favorite") {
        dispatch(
          openMobileViewDialog({
            isOpen: true,
            title: "Want to Favorite this Wheel?",
            type: "favorite",
            wheel: wheelData,
          })
        );
      } else if (type === "clone") {
        dispatch(
          openMobileViewDialog({
            isOpen: true,
            title: "Want to Clone this Wheel?",
            type: "clone",
            wheel: wheelData,
          })
        );
      }

      return;
    }
  };

  return (
    <>
      <Styled.Background
        color={segmentBgColor}
        currentBg={currentBg}
        bgWallpaper={bgWallpaper}
        wallpaperTransparency={wallpaperTransparency}
        wallpaperHuePercentage={wallpaperHuePercentage}
        wallpaperHueColor={wallpaperHueColor}
      />
      <ShareInviteModal
        isVisible={inviteShareModalVisible}
        onClose={() => setInviteShareModalVisible(false)}
      />
      <Styled.Wrapper ref={wheelRef}>
        <Styled.Header hideView={!!selectedArc || openSegmentList}>
          <SearchHeader
            onClickBeta={() => {
              setInviteShareModalVisible(true);
              handleMobileViewModal({ type: "default" });
            }}
            handleNavigate={() => handleMobileViewModal({ type: "default" })}
            navigateToSignup={navigateToSignup}
          />
          <Styled.IconsHeader>
            {iconComponents.map((icon) => (
              <Styled.IconWrapper
                key={icon.id}
                style={{
                  background:
                    selectedIcon === icon.id && icon.id !== "like"
                      ? icon.color
                      : colors.white,
                }}
                onClick={() => {
                  if (icon.id === "like") {
                    handleLikeUnlike();
                    return;
                  }
                  if (icon.id === "clone") {
                    handleCloneWheel();
                  }
                  setSelectedIcon(icon.id);
                }}
              >
                {icon.id === "like" && isLiked ? (
                  <FilledHeart />
                ) : (
                  React.cloneElement(icon.component, {
                    color: selectedIcon === icon.id ? colors.white : icon.color,
                  })
                )}
              </Styled.IconWrapper>
            ))}
          </Styled.IconsHeader>
        </Styled.Header>
        {isHiddenWheel ? (
          renderHiddenWheelView()
        ) : (
          <Styled.ContentView>
            {notification.show && (
              <Styled.NotificationBar>
                <Styled.TextContainer style={{ color: notification.color }}>
                  <TextView variant="subtitle" style={{ fontWeight: 700 }}>
                    {notification.text}
                  </TextView>
                  <Close onClick={() => setNotification(emptyNotification)} />
                </Styled.TextContainer>
              </Styled.NotificationBar>
            )}
            {selectedIcon === "description" && (
              <WheelDescription
                data={wheelData}
                userProfile={userProfile}
                onClick={toggleSegmentList}
              />
            )}

            {selectedIcon === "business" && <BusinessBox />}
            {selectedIcon === "hotbar" && <NewHotbar isOpen={true} />}
            {selectedIcon === "share" && (
              <ShareModal
                userData={userData}
                chartData={wheelSegments}
                wheel={wheelData}
                color={segmentBgColor}
                currentBg={currentBg}
                bgWallpaper={bgWallpaper}
                wallpaperTransparency={wallpaperTransparency}
                wallpaperHuePercentage={wallpaperHuePercentage}
                wallpaperHueColor={wallpaperHueColor}
                handleClose={() => setSelectedIcon(iconComponents[0].id)}
              />
            )}

            {!openSegmentList && (
              <Styled.ToggleLabelIconContainer onClick={handleToggleLabels}>
                <Styled.ToggleLabelIcon
                  src={
                    showLabels
                      ? ASSETS.LableToggleIconMobileFilled
                      : ASSETS.LableToggleIconMobile
                  }
                  className="img-fluid"
                  alt="Toggle Labels"
                />
              </Styled.ToggleLabelIconContainer>
            )}

            <Styled.WheelView
              style={{
                marginTop: showLabels && !openSegmentList ? "5vh" : "2vh",
                opacity: wheelViewOpacity, // Set opacity based on scroll
                transition: "opacity 0.5s ease", // Fade-in animation
              }}
              selectedArc={!!selectedArc}
            >
              {openSegmentList ? (
                <SegmentsList
                  wheelSegments={wheelSegments}
                  onClose={toggleSegmentList}
                  onClickLabel={onClickLabel}
                  selectedArc={!!selectedArc}
                />
              ) : (
                <WheelComponent
                  height={
                    portraitScreenRatio >= 1.5
                      ? showLabels
                        ? "64vw"
                        : "94vw"
                      : "70vw"
                  }
                  width={
                    portraitScreenRatio >= 1.5
                      ? showLabels
                        ? "64vw"
                        : "94vw"
                      : "70vw"
                  }
                  chartData={wheelSegments}
                  setChartData={handleSetChartData}
                  showLabels={showLabels}
                  config={{
                    isNotDraggable: true,

                    segmentLableLimit: 10,
                    inactiveOpacity: 0.5,
                  }}
                  userData={userData}
                />
              )}
            </Styled.WheelView>
            {selectedArc && (
              <Editor enabled={false}>
                <SegmentView
                  onClickNext={onClickNext}
                  onClickPrev={onClickPrev}
                  selectedArc={selectedArc}
                  onClose={() => {
                    setWheelViewOpacity(1);
                    onClose();
                  }}
                  openSegmentList={openSegmentList}
                  showBackIcon={wheelViewOpacity === 0}
                  onClickBack={scrollToTop}
                />
              </Editor>
            )}
          </Styled.ContentView>
        )}
        <GeneralDialog />
        <MobileViewDialog wheelData={wheelData} />
        <WaitlistFormModal
          open={waitListModalVisible}
          onClose={() => setWaitListModalVisible(false)}
          wheelData={wheelData}
          onSuccess={() => {
            setNotification({
              ...notification,
              show: true,
              text: "BETA waitlist account is created!",
            });
            setTimeout(() => setNotification(emptyNotification), 5000);
          }}
        />
        <Popup
          disableClose={false}
          open={openPopup}
          handleClose={handlePopUpClose}
          wheelId={wheelData?.id}
        />
      </Styled.Wrapper>
    </>
  );
}
